/**
 * Maps d’affichage Matériels V2 — présentation pure, aucune logique métier.
 * Source de vérité = champs API (status, health.health, type, capabilities).
 */

export const LIFECYCLE_LABELS = {
  EN_STOCK: 'En stock',
  AFFECTE: 'Affecté',
  RETOUR: 'Retour',
  HORS_SERVICE: 'Hors service',
};

export const DIAGNOSTIC_LABELS = {
  OK: 'Opérationnel',
  CHECK: 'À vérifier',
  ERROR: 'Erreur',
};

export const TYPE_LABELS = {
  PRINTER_CLOUD: 'Imprimante cloud',
  PRINTER_LOCAL: 'Imprimante locale',
  SCANNER: 'Scanner',
  PAYMENT_TERMINAL: 'Terminal de paiement',
  POS: 'Caisse',
  DISPLAY: 'Écran / afficheur',
  TABLET: 'Tablette',
  OTHER: 'Autre',
};

export const CAPABILITY_LABELS = {
  CLOUD_PRINT: 'Impression cloud',
  LOCAL_PRINT: 'Impression locale',
  SCAN: 'Scan',
  PAYMENT: 'Paiement',
  DISPLAY: 'Affichage',
};

/** @param {string|null|undefined} status */
export function lifecycleLabel(status) {
  if (!status) return LIFECYCLE_LABELS.EN_STOCK;
  return LIFECYCLE_LABELS[status] || 'Statut inconnu';
}

/**
 * Diagnostic UI — uniquement pertinent si Affecté.
 * @param {{ status?: string, health?: { health?: string|null }|null }} item
 * @returns {'Opérationnel'|'À vérifier'|'Erreur'|null}
 */
export function diagnosticLabel(item) {
  if (!item || item.status !== 'AFFECTE') return null;
  const raw = item.health?.health;
  if (raw === 'OK') return DIAGNOSTIC_LABELS.OK;
  if (raw === 'ERROR') return DIAGNOSTIC_LABELS.ERROR;
  // CHECK, null, absent → À vérifier (label UI, pas de recalcul métier)
  return DIAGNOSTIC_LABELS.CHECK;
}

/** @param {string|null|undefined} type */
export function typeLabel(type) {
  if (!type) return TYPE_LABELS.OTHER;
  return TYPE_LABELS[type] || TYPE_LABELS.OTHER;
}

/** @param {string[]|null|undefined} caps */
export function capabilityLabels(caps) {
  if (!Array.isArray(caps) || caps.length === 0) return [];
  return caps
    .map((c) => CAPABILITY_LABELS[c])
    .filter(Boolean);
}

/**
 * Agrégat filtre « À traiter » (Affectés en Erreur ou À vérifier).
 * @param {{ status?: string, health?: { health?: string|null }|null }} item
 */
export function isATraiter(item) {
  if (!item || item.status !== 'AFFECTE') return false;
  const diag = diagnosticLabel(item);
  return diag === DIAGNOSTIC_LABELS.ERROR || diag === DIAGNOSTIC_LABELS.CHECK;
}

/**
 * Titre humain : label → model → type UI.
 * @param {{ label?: string|null, model?: string|null, type?: string|null }} item
 */
export function deviceTitle(item) {
  const label = item?.label && String(item.label).trim();
  if (label) return label;
  const model = item?.model && String(item.model).trim();
  if (model) return model;
  return typeLabel(item?.type);
}

/**
 * Point de vente affiché — jamais l’id.
 * @param {{ commercant?: { nom?: string|null }|null, status?: string }} item
 */
export function pointOfSaleLabel(item) {
  const nom = item?.commercant?.nom && String(item.commercant.nom).trim();
  if (nom) return nom;
  return 'Non affecté';
}

/**
 * @param {string|Date|null|undefined} iso
 * @returns {string}
 */
export function formatRelativeActivity(iso) {
  if (!iso) return 'Aucun appel cloud';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Aucun appel cloud';
  const diffMs = Date.now() - d.getTime();
  const abs = Math.abs(diffMs);
  const minutes = Math.floor(abs / 60000);
  if (minutes < 1) return 'Appel cloud · à l’instant';
  if (minutes < 60) return `Appel cloud · il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Appel cloud · il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `Appel cloud · il y a ${days} j`;
  try {
    return `Appel cloud · ${d.toLocaleDateString('fr-FR')}`;
  } catch {
    return 'Appel cloud · date inconnue';
  }
}

export function activityFromItem(item) {
  return formatRelativeActivity(item?.health?.lastJournalAt);
}

export const PROVIDER_LABELS = {
  SUNMI: 'Sunmi',
  OTHER: 'Autre',
};

/** @param {string|null|undefined} provider */
export function providerLabel(provider) {
  if (!provider) return '—';
  return PROVIDER_LABELS[provider] || String(provider);
}

/**
 * Date affichable FR — jamais d’ISO brut.
 * @param {string|Date|null|undefined} iso
 */
export function formatDisplayDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Date inconnue';
  try {
    return d.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Date inconnue';
  }
}

/** @param {boolean|null|undefined} complete */
export function cloudConfigLabel(complete) {
  if (complete === true) return 'Complète';
  return 'À compléter';
}

/** @param {boolean|null|undefined} hasKey */
export function cloudKeyLabel(hasKey) {
  if (hasKey === true) return 'Présente';
  return 'Absente';
}

export const JOURNAL_TRIGGER_LABELS = {
  TEST: 'Test manuel',
  VALIDEE: 'Commande validée',
  ACCEPTEE: 'Commande acceptée',
  CAISSE: 'Impression caisse',
};

/** @param {string|null|undefined} trigger */
export function journalTriggerLabel(trigger) {
  if (!trigger) return 'Déclencheur inconnu';
  return JOURNAL_TRIGGER_LABELS[trigger] || 'Autre déclencheur';
}

/** @param {boolean} success */
export function journalOutcomeLabel(success) {
  return success ? 'Succès' : 'Échec';
}

/** @param {boolean} testMode */
export function journalTestModeLabel(testMode) {
  return testMode ? 'Mode test' : null;
}

/**
 * Nettoie un message journal pour un affichage support (masque tokens / IDs techniques).
 * @param {string|null|undefined} message
 * @returns {string|null}
 */
export function sanitizeJournalMessage(message) {
  if (message == null) return null;
  let s = String(message).trim();
  if (!s) return null;
  s = s.replace(/Bearer\s+\S+/gi, '[jeton masqué]');
  s = s.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi,
    '[réf.]'
  );
  s = s.replace(/\bcm[a-z0-9]{20,}\b/gi, '[réf.]');
  s = s.replace(/\bsk[_-][a-zA-Z0-9]{8,}\b/g, '[secret]');
  return s.trim() || null;
}

/**
 * Dernier appel cloud — libellé court pour fiche (sans préfixe redondant si label externe).
 * @param {object} item
 */
export function lastCloudCallLabel(item) {
  const iso = item?.health?.lastJournalAt;
  if (!iso) return 'Aucun';
  const full = formatRelativeActivity(iso);
  return full.replace(/^Appel cloud ·\s*/, '') || 'Aucun';
}
